// Equipment Library workflows — P2-C (docs/implementation/P2_ASSUMED_EQUIPMENT.md).
// Pure payload builders and a diff for the two explicit library actions:
//
//   ADD FROM LIBRARY  — an independent PROJECT SNAPSHOT: the library item's
//   header defaults + its library connections are copied into ProcessEquipment
//   + EquipmentConnections (sp_SaveProcessEquipment, all connections as new
//   rows). LibraryItemID is retained as PROVENANCE ONLY; nothing on the project
//   row is ever re-read from the library afterwards, so later library edits
//   cannot modify existing project records.
//
//   SAVE TO LIBRARY   — never implicit. A project row with no LibraryItemID can
//   only CREATE a new library item. A row that carries a LibraryItemID offers
//   two explicit choices — UPDATE the linked item, or CREATE a new one — and
//   the update path shows the material changes (libraryDiff) before saving.
//   Updating a library item changes defaults for FUTURE projects only: every
//   existing project row is its own copy, so nothing already estimated moves.
//   (A future compare/update-from-newer-defaults workflow is out of scope;
//   P2-C implements no live synchronization.)
//
// sp_SaveEquipmentLibraryItem contract (db/24): header fields written as sent
// — including WidthIn/DepthIn/HeightIn/WeightLbs, which ProcessEquipment does
// not carry, so an UPDATE must pass the library item's current dimensions
// through or it would blank them; `connections[]` REPLACES the item's library
// connections wholesale (DELETE then INSERT). Library connections carry no
// DiversityOn / AssemblyID / RouteLF — those are project-only columns.

import { utilityLabel } from "../config/utilityTypes";

const num = (v) => { if (v === null || v === undefined || v === "") return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
const str = (v) => { const s = v == null ? "" : String(v).trim(); return s || null; };

export const libraryConnectionsOf = (item, libraryConnections = []) =>
  libraryConnections.filter(c => c.LibraryItemID === item.LibraryItemID);

// Library connection row -> sp_SaveProcessEquipment `connections[]` entry (new row).
export function libraryConnectionToProjectPayload(c) {
  return {
    connection_id: null,
    utility_type: c.UtilityType, circuit_desc: str(c.CircuitDesc),
    flow_value: num(c.FlowValue), flow_units: str(c.FlowUnits), pressure_value: num(c.PressureValue), pressure_units: str(c.PressureUnits),
    branch_size: str(c.BranchSize), consumption_per_day: num(c.ConsumptionPerDay), consumption_units: str(c.ConsumptionUnits),
    volts: str(c.Volts), phase: num(c.Phase), fla: num(c.FLA), mca: num(c.MCA), mocp: num(c.MOCP), hp: num(c.HP), kw: num(c.KW),
    power_class: str(c.PowerClass), usage_factor: num(c.UsageFactor), heat_gain_btuh: num(c.HeatGainBTUH),
    diversity_on: 1, auto_manual: null, assembly_id: null, route_lf: null,
    notes: str(c.Notes), display_order: num(c.DisplayOrder) ?? 100,
  };
}

// ADD FROM LIBRARY -> independent project snapshot.
export function libraryItemToProjectPayload(item, libraryConnections = [], { revisionId, roomId = null, locationText = null, qty = 1, tag = null, displayOrder = 100 } = {}) {
  if (revisionId == null) throw new Error("libraryItemToProjectPayload: revisionId is required");
  return {
    equipment_id: null, revision_id: revisionId,
    tag: str(tag), description: str(item.Description) || "New Equipment", category: str(item.Category),
    manufacturer: str(item.Manufacturer), model: str(item.Model),
    qty: Math.max(1, num(qty) ?? 1), standby_units: 0,
    room_id: roomId ?? null, location_text: roomId != null ? null : str(locationText),
    status: "new", furnished_by: null, installed_by: null,
    library_item_id: item.LibraryItemID,        // provenance only
    // P2 Phase B: the library's dimensions become the project's own snapshot
    // (editable there; a later library edit never reaches it); a library copy
    // is still an estimating assumption until someone verifies it.
    width_in: num(item.WidthIn), depth_in: num(item.DepthIn), height_in: num(item.HeightIn), weight_lbs: num(item.WeightLbs),
    assumption_status: "assumed",
    notes: item.Notes ?? null, display_order: displayOrder,
    connections: libraryConnectionsOf(item, libraryConnections).map(libraryConnectionToProjectPayload),
    deleted_connection_ids: [],
  };
}

// Project connection row -> sp_SaveEquipmentLibraryItem `connections[]` entry.
export function projectConnectionToLibraryPayload(c) {
  return {
    utility_type: c.UtilityType, circuit_desc: str(c.CircuitDesc),
    flow_value: num(c.FlowValue), flow_units: str(c.FlowUnits), pressure_value: num(c.PressureValue), pressure_units: str(c.PressureUnits),
    branch_size: str(c.BranchSize), consumption_per_day: num(c.ConsumptionPerDay), consumption_units: str(c.ConsumptionUnits),
    volts: str(c.Volts), phase: num(c.Phase), fla: num(c.FLA), mca: num(c.MCA), mocp: num(c.MOCP), hp: num(c.HP), kw: num(c.KW),
    power_class: str(c.PowerClass), usage_factor: num(c.UsageFactor), heat_gain_btuh: num(c.HeatGainBTUH),
    notes: str(c.Notes), display_order: num(c.DisplayOrder) ?? 100,
  };
}

// SAVE TO LIBRARY. mode 'create' -> a new library item (no library_item_id).
// mode 'update' -> the linked item. Dimensions: the PROJECT record's own
// snapshot (P2 Phase B) wins; where the project has none, 'update' keeps the
// item's current value (the proc writes dimensions as sent, so an omitted one
// would blank it) and 'create' leaves it empty.
export function projectEquipmentToLibraryPayload(eq, connections = [], { mode, libraryItem = null } = {}) {
  if (mode !== "create" && mode !== "update") throw new Error("projectEquipmentToLibraryPayload: mode must be 'create' or 'update' — the choice is never implicit");
  if (mode === "update" && (!libraryItem || libraryItem.LibraryItemID == null)) throw new Error("projectEquipmentToLibraryPayload: update requires the linked library item");
  const mine = connections.filter(c => c.EquipmentID === eq.EquipmentID);
  return {
    library_item_id: mode === "update" ? libraryItem.LibraryItemID : null,
    category: str(eq.Category), description: str(eq.Description) || "New Item",
    manufacturer: str(eq.Manufacturer), model: str(eq.Model),
    width_in:   num(eq.WidthIn)   ?? (mode === "update" ? num(libraryItem.WidthIn)   : null),
    depth_in:   num(eq.DepthIn)   ?? (mode === "update" ? num(libraryItem.DepthIn)   : null),
    height_in:  num(eq.HeightIn)  ?? (mode === "update" ? num(libraryItem.HeightIn)  : null),
    weight_lbs: num(eq.WeightLbs) ?? (mode === "update" ? num(libraryItem.WeightLbs) : null),
    notes: eq.Notes ?? null,
    connections: mine.map(projectConnectionToLibraryPayload),
  };
}

// Which Save-to-Library choices a project row may be offered.
//   no LibraryItemID                     -> ['create']
//   LibraryItemID present in the library -> ['update', 'create']
//   LibraryItemID not in the (active) library -> ['create'] (the linked item is retired/missing)
export function saveToLibraryChoices(eq, libraryById) {
  if (eq?.LibraryItemID == null) return { choices: ["create"], linked: null, linkedMissing: false };
  const linked = libraryById?.get?.(eq.LibraryItemID) || null;
  return linked ? { choices: ["update", "create"], linked, linkedMissing: false } : { choices: ["create"], linked: null, linkedMissing: true };
}

// ---- diff for the update confirmation ----------------------------------------------------------

// [label, field, keepWhenBlank] — a dimension the project record has NOT
// filled in is not a change (Save-to-library keeps the item's value there).
const HEADER_FIELDS = [["Category", "Category"], ["Description", "Description"], ["Manufacturer", "Manufacturer"], ["Model", "Model"],
  ["Width (in)", "WidthIn", true], ["Depth (in)", "DepthIn", true], ["Height (in)", "HeightIn", true], ["Weight (lbs)", "WeightLbs", true], ["Notes", "Notes"]];
const CONN_FIELDS = ["UtilityType", "CircuitDesc", "FlowValue", "FlowUnits", "PressureValue", "PressureUnits", "BranchSize", "ConsumptionPerDay", "ConsumptionUnits", "Volts", "Phase", "FLA", "MCA", "MOCP", "HP", "KW", "PowerClass", "UsageFactor", "HeatGainBTUH"];
const normalize = (v) => { const n = num(v); if (n !== null) return String(n); return str(v) ?? ""; };

// Library connections have no identity shared with project connections, so
// they are compared as multisets of material values.
export function connectionSignature(c) {
  return CONN_FIELDS.map(f => `${f}=${normalize(c?.[f])}`).join("|");
}
export function connectionSummary(c) {
  const bits = [utilityLabel(c.UtilityType)];
  if (c.CircuitDesc) bits.push(`(${c.CircuitDesc})`);
  if (c.UtilityType === "electrical") {
    if (c.Volts) bits.push(`${c.Volts}V${c.Phase ? ` ${c.Phase}φ` : ""}`);
    if (num(c.KW) !== null) bits.push(`${num(c.KW)} kW`);
    else if (num(c.HP) !== null) bits.push(`${num(c.HP)} HP`);
    if (num(c.FLA) !== null) bits.push(`${num(c.FLA)} FLA`);
  } else {
    if (num(c.FlowValue) !== null) bits.push(`${num(c.FlowValue)} ${c.FlowUnits || ""}`.trim());
    if (num(c.PressureValue) !== null) bits.push(`@ ${num(c.PressureValue)} ${c.PressureUnits || ""}`.trim());
  }
  if (num(c.HeatGainBTUH) !== null) bits.push(`${num(c.HeatGainBTUH)} BTUH`);
  return bits.join(" ");
}

// Material changes an UPDATE would write to the linked library item.
//   { header: [{ field, from, to }], connections: { added: [row], removed: [row], unchanged: n }, hasChanges }
export function libraryDiff(eq, projectConnections = [], libraryItem, libraryConnections = []) {
  const header = [];
  for (const [label, field, keepWhenBlank] of HEADER_FIELDS) {
    const from = normalize(libraryItem?.[field]), to = normalize(eq?.[field]);
    if (keepWhenBlank && !to) continue;
    if (from !== to) header.push({ field: label, from: from || null, to: to || null });
  }
  const mine = projectConnections.filter(c => c.EquipmentID === eq.EquipmentID);
  const lib = libraryConnectionsOf(libraryItem || {}, libraryConnections);
  const libSigs = new Map();
  for (const c of lib) libSigs.set(connectionSignature(c), [...(libSigs.get(connectionSignature(c)) || []), c]);
  const added = [];
  let unchanged = 0;
  for (const c of mine) {
    const sig = connectionSignature(c);
    const bucket = libSigs.get(sig);
    if (bucket?.length) { bucket.pop(); unchanged += 1; } else added.push(c);
  }
  const removed = [...libSigs.values()].flat();
  return { header, connections: { added, removed, unchanged }, hasChanges: header.length > 0 || added.length > 0 || removed.length > 0 };
}
